import Dexie, { type Table } from 'dexie';
import type { BarcodeFormat, EcLevel } from './barcode.ts';

/**
 * The device's copy of the wallet. Everything the UI shows is read from here, so a card opens
 * with the network switched off; the server is a backup and a way onto a new device, never a
 * step on the path to displaying a barcode.
 */
export interface LocalCard {
  id: string;
  name: string;
  barcodeValue: string;
  barcodeFormat: BarcodeFormat;
  /** QR / Micro QR error-correction level read from the printed code; null when unknown or typed in. */
  barcodeEcLevel?: EcLevel | null;
  /** Expiry printed on the card, as an ISO date (YYYY-MM-DD). Drives the expiry notifications. */
  expiresOn?: string | null;
  color: string | null;
  note: string | null;
  favorite: boolean;
  lastUsedAt: number | null;
  createdAt: number;
  updatedAt: number;
  deleted: boolean;
  /** 1 while the local copy is ahead of the server. Indexed so the push query is cheap. */
  dirty: 0 | 1;
}

export type ImageSide = 'front' | 'back';

export interface LocalImage {
  /** `${cardId}:${side}` — Dexie needs a single-value primary key for `put`. */
  key: string;
  cardId: string;
  side: ImageSide;
  /**
   * Raw bytes rather than a Blob. IndexedDB can hold a Blob, but Safari has a history of
   * mangling them, and bytes plus a mime type reconstruct into a Blob anywhere.
   */
  bytes: ArrayBuffer | null;
  mime: string;
  updatedAt: number;
  deleted: boolean;
  dirty: 0 | 1;
}

/**
 * A place where the card has been used. When the app opens near one, the card opens by itself.
 * Synced like cards: last write wins on updatedAt, deletes travel as tombstones.
 */
export interface LocalPlace {
  id: string;
  cardId: string;
  lat: number;
  lng: number;
  /** Metres, as reported by the device when the place was saved. */
  accuracy: number | null;
  label: string | null;
  createdAt: number;
  updatedAt: number;
  deleted: boolean;
  dirty: 0 | 1;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

/** A 32-character id, safe in URLs and accepted by the server's id rules. */
export const newId = (): string =>
  (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`)
    .replace(/-/g, '').slice(0, 32);

export const imageKey = (cardId: string, side: ImageSide) => `${cardId}:${side}`;

export class KlarnusDB extends Dexie {
  cards!: Table<LocalCard, string>;
  images!: Table<LocalImage, string>;
  meta!: Table<MetaRow, string>;
  places!: Table<LocalPlace, string>;

  constructor(name = 'klarnus') {
    super(name);
    this.version(1).stores({
      cards: 'id, updatedAt, dirty, deleted, lastUsedAt',
      images: 'key, cardId, dirty, updatedAt',
      meta: 'key',
    });
    this.version(2).stores({
      places: 'id, cardId, dirty, updatedAt',
    });
  }
}

export const db = new KlarnusDB();

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row === undefined ? fallback : (row.value as T);
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/** Cards the list should show: not deleted, most recently used first, then newest. */
export async function listCards(): Promise<LocalCard[]> {
  const all = await db.cards.filter((c) => !c.deleted).toArray();
  return all.sort((a, b) => {
    if (a.favorite !== b.favorite) return a.favorite ? -1 : 1;
    const au = a.lastUsedAt ?? 0;
    const bu = b.lastUsedAt ?? 0;
    if (au !== bu) return bu - au;
    return b.createdAt - a.createdAt;
  });
}

export async function saveCard(card: LocalCard): Promise<void> {
  await db.cards.put({ ...card, updatedAt: Date.now(), dirty: 1 });
}

export async function touchCard(id: string): Promise<void> {
  const card = await db.cards.get(id);
  if (!card) return;
  await db.cards.put({ ...card, lastUsedAt: Date.now(), updatedAt: Date.now(), dirty: 1 });
}

/** Deleting keeps a tombstone: the server has to learn about it, and so do other devices. */
export async function deleteCard(id: string): Promise<void> {
  const card = await db.cards.get(id);
  if (!card) return;
  await db.cards.put({ ...card, deleted: true, updatedAt: Date.now(), dirty: 1 });
  const sides: ImageSide[] = ['front', 'back'];
  for (const side of sides) {
    const img = await db.images.get(imageKey(id, side));
    if (img) await db.images.put({ ...img, bytes: null, deleted: true, updatedAt: Date.now(), dirty: 1 });
  }
  await forgetPlaces(id);
}

/** Places of one card that are still in use. */
export async function listPlaces(cardId?: string): Promise<LocalPlace[]> {
  const rows = cardId ? await db.places.where('cardId').equals(cardId).toArray() : await db.places.toArray();
  return rows.filter((p) => !p.deleted).sort((a, b) => b.createdAt - a.createdAt);
}

export async function addPlace(place: Omit<LocalPlace, 'createdAt' | 'updatedAt' | 'deleted' | 'dirty'>): Promise<void> {
  const now = Date.now();
  await db.places.put({ ...place, createdAt: now, updatedAt: now, deleted: false, dirty: 1 });
}

export async function forgetPlace(id: string): Promise<void> {
  const place = await db.places.get(id);
  if (place) await db.places.put({ ...place, deleted: true, updatedAt: Date.now(), dirty: 1 });
}

/** "Dimentica tutti i posti" for one card. Tombstones, so other devices forget them too. */
export async function forgetPlaces(cardId: string): Promise<void> {
  const now = Date.now();
  const rows = await db.places.where('cardId').equals(cardId).toArray();
  await db.places.bulkPut(rows.filter((p) => !p.deleted).map((p) => ({ ...p, deleted: true, updatedAt: now, dirty: 1 as const })));
}

export async function putImage(cardId: string, side: ImageSide, blob: Blob): Promise<void> {
  await db.images.put({
    key: imageKey(cardId, side), cardId, side, bytes: await blob.arrayBuffer(),
    mime: blob.type || 'image/jpeg', updatedAt: Date.now(), deleted: false, dirty: 1,
  });
}

export async function removeImage(cardId: string, side: ImageSide): Promise<void> {
  await db.images.put({
    key: imageKey(cardId, side), cardId, side, bytes: null,
    mime: '', updatedAt: Date.now(), deleted: true, dirty: 1,
  });
}

export async function getImage(cardId: string, side: ImageSide): Promise<Blob | null> {
  const row = await db.images.get(imageKey(cardId, side));
  if (!row || row.deleted || !row.bytes) return null;
  return new Blob([row.bytes], { type: row.mime || 'image/jpeg' });
}

/** Wipes the device copy — used on logout, so the next account does not inherit these cards. */
export async function clearLocalData(): Promise<void> {
  await Promise.all([db.cards.clear(), db.images.clear(), db.meta.clear(), db.places.clear()]);
}
